import { WarningCircleIcon } from "@phosphor-icons/react";
import { ReadFailedCard } from "@bednbody/ui/components/read-failed-card";
import { Link, useLocation } from "react-router-dom";
import { drillInState } from "@/lib/drillIn";
import { timeAgo } from "@/lib/time";

export interface AttentionItem {
  id: string;
  /** Short uppercase category chip, e.g. RIDER / OVERDUE / NO-SHOW / CASH. */
  tag: string;
  tone: "danger" | "warning";
  text: string;
  /** ISO timestamp the item refers to (renders as "Xm ago"); omit to hide. */
  at?: string;
  /** Where the row links; omit for roles that can't visit the target route. */
  href?: string;
}

// One severity signal per row: the tag chip. A colored dot next to a colored
// tag saying the same thing is decoration, not information.
const TONE_STYLES: Record<AttentionItem["tone"], string> = {
  danger: "bg-[var(--destructive)]/10 text-[var(--destructive)]",
  warning: "bg-[var(--warning)]/15 text-[var(--warning)]",
};

/**
 * The hub manager's intervention queue (User Stories — Admin §Hub Manager:
 * "alerted on no-show reports and session-start overdue"). Purely
 * presentational — the page decides what counts as an item.
 */
export function NeedsAttention({
  items,
  maxItems,
  failed = false,
}: {
  items: AttentionItem[];
  maxItems?: number;
  /** A failed read is not a clear board. */
  failed?: boolean;
}) {
  const location = useLocation();
  // The most dangerous empty state on the page: this one exists to say
  // "nothing is wrong", which is the exact sentence a failed read must never
  // be allowed to produce.
  if (failed) {
    return (
      <ReadFailedCard message="The bookings and payment checks behind this list could not be read. Nothing here has been ruled out." />
    );
  }

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing needs your attention right now.</p>;
  }

  const visible = maxItems ? items.slice(0, maxItems) : items;
  const remainder = items.length - visible.length;

  return (
    <div className="divide-y divide-border">
      {visible.map((item) => {
        const row = (
          <>
            <span
              className={`shrink-0 rounded px-1.5 py-0.5 font-mono type-body-micro font-semibold uppercase ${TONE_STYLES[item.tone]}`}
            >
              {item.tag}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">{item.text}</span>
            {item.at ? (
              <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(item.at)}</span>
            ) : null}
          </>
        );
        const rowClass = "flex items-center gap-3 py-2.5 transition-colors hover:bg-card-hover";
        return item.href ? (
          <Link
            key={item.id}
            to={item.href}
            state={drillInState(location.pathname)}
            className={rowClass}
          >
            {row}
          </Link>
        ) : (
          <div key={item.id} className={rowClass}>
            {row}
          </div>
        );
      })}
      {remainder > 0 ? (
        <p className="pt-2 text-xs text-muted-foreground">+{remainder} more</p>
      ) : null}
    </div>
  );
}

export const NeedsAttentionIcon = WarningCircleIcon;
